import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Plus, Trash2, Pencil } from 'lucide-react';
import { useApp } from '../context/AppContext';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { api } from '../lib/api';

const Agents = () => {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [cat, setCat] = useState(t.agents.categories[0]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listAgents().then(setAll).catch(() => toast.error(lang === 'fr' ? 'Impossible de charger les agents' : 'Could not load agents')).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const catValue = (a) => (lang === 'fr' ? a.category : (a.categoryEn || a.category));
  const filtered = cat === t.agents.categories[0] ? all : all.filter((a) => catValue(a) === cat);

  const runAgent = (a) => {
    const name = lang === 'fr' ? a.name : (a.nameEn || a.name);
    navigate('/chat', { state: { agent: a, initial: `${lang === 'fr' ? 'Bonjour, tu es' : 'Hi, you are'} ${name}. ${lang === 'fr' ? 'Peux-tu te présenter et m\u2019aider ?' : 'Can you introduce yourself and help me?'}` } });
  };

  const removeAgent = async (a) => {
    try {
      await api.deleteAgent(a.id);
      setAll((prev) => prev.filter((x) => x.id !== a.id));
      toast.success(lang === 'fr' ? 'Agent supprimé' : 'Agent deleted');
    } catch { toast.error(lang === 'fr' ? 'Suppression impossible' : 'Could not delete'); }
  };

  return (
    <div className="flex bg-background" data-testid="agents-page">
      <AppSidebar conversations={[]} onNewChat={() => navigate('/chat')} />
      <main className="flex-1 h-screen overflow-y-auto min-w-0">
        <div className="h-16 border-b border-border flex items-center justify-between px-5 sm:px-8 sticky top-0 bg-background/80 backdrop-blur z-10">
          <h1 className="font-display text-xl font-bold">{t.agents.title}</h1>
          <Button onClick={() => navigate('/build')} className="rounded-lg font-semibold gap-2" data-testid="create-agent-btn"><Plus className="h-4 w-4" /> {t.agents.create}</Button>
        </div>

        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          <p className="text-muted-foreground">{t.agents.subtitle}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {t.agents.categories.map((c) => (
              <button key={c} data-testid={`category-filter-${c}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === c ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {['s1', 's2', 's3', 's4', 's5', 's6'].map((k) => <div key={k} className="h-52 rounded-2xl border border-border bg-card animate-pulse" />)}
            </div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agents-grid">
              {filtered.map((a) => {
                const Icon = Icons[a.icon] || Icons.Bot;
                const name = lang === 'fr' ? a.name : (a.nameEn || a.name);
                const desc = lang === 'fr' ? a.desc : (a.descEn || a.desc);
                return (
                  <div key={a.id} data-testid={`agent-card-${a.id}`} className="group relative rounded-2xl border border-border bg-card p-6 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1 flex flex-col">
                    {!a.is_builtin && (
                      <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => navigate(`/build/${a.id}`)} data-testid={`edit-agent-${a.id}`} className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title={lang === 'fr' ? 'Modifier' : 'Edit'}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => removeAgent(a)} data-testid={`delete-agent-${a.id}`} className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title={lang === 'fr' ? 'Supprimer' : 'Delete'}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-lg">{name}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">{catValue(a)}</span>
                      <Button onClick={() => runAgent(a)} data-testid={`use-agent-${a.id}`} variant="ghost" size="sm" className="rounded-lg font-semibold text-primary hover:text-primary hover:bg-primary/10 gap-1">
                        {t.agents.use} <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Agents;
